import { User } from '/@src/models/user'
import {
  ContentLevel,
  ContentStatusValue,
  LiveStatus,
  MediaMeta,
  Platform,
  TagRelations,
  WordsFilter,
} from '/@src/models/content'
import { TagRelation } from '/@src/models/graphApi'
import { Order, PayChannel } from '/@src/models/order'
import { Ref } from 'vue'
import { DocumentNode } from 'graphql'
import { UseQueryOptions, UseQueryReturn } from '@vue/apollo-composable'
import { ApolloError } from '@apollo/client'
import { OperationVariables } from '@apollo/client/core'

export interface CommonResponse {
  code: number
  status: number
  data: any
  message?: string
}

export interface SourceResponse<T> {
  status: number
  data: T
  msg?: string
}

export interface DataResponse<T> {
  status: number
  data: T
  msg?: string
}
export interface KVResult<T> {
  auth: string
  key: string
  name: string
  value: T
}

export interface PushResult<T> {
  extra: NotificationParamsExtra
  app_key: string
  created_time: string
  dismiss_count: number
  err_code: string
  err_msg: string
  expire_time: string
  open_count: number
  platform: string
  sent_count: number
  start_time: string
  status: number
  task_id: string
  title: string
  total_count: number
}

export interface CommonRequestParam {
  id?: number
}
/** 分页列表请求参数 */
export interface PageRequestParam extends CommonRequestParam {
  /** 分页标记 */
  cursor?: string
  anchor?: string
  offset?: number
  /** 请求数量 */
  limit: number
  page?: number
}
export interface ArticleListRequestParam extends PageRequestParam {
  tag?: number
  userId?: number
  status?: ContentStatus
  statusValue?: ContentStatusValue
  articleType?: string
  contentLevel?: ContentLevel
  keywords?: string
  wordsFilter?: WordsFilter
}
export interface ContentListRequestParam extends PageRequestParam {
  tag?: number
  userId?: number
  status?: ContentStatus
  keywords?: string
  start?: number
  end?: number
  sort?: string
}
export interface SearchRequestParam extends PageRequestParam {
  keywords?: string
  track?: string
  status?: ContentStatus | ContentUpperStatus
  statusValue?: ContentStatusValue
  tag?: number
  userId?: number
  on_sale?: boolean
  mobile?: number
}
export interface SearchUserParam extends SearchRequestParam {
  userStatus?: UserStatus
  ids?: number[]
}
export interface LiveRequestParam extends PageRequestParam {
  status?: LiveStatus
}
export interface MessageRequestParam extends PageRequestParam {
  action: MessageAction
}

export interface TagRelationParams {
  contentId: number
  kind: ContentKind
  tagRelations: TagRelation[]
}
export enum ContentStatus {
  PUBLISHED = 'published',
  DRAFT = 'draft',
  DELETED = 'deleted',
}

export enum ContentUpperStatus {
  PUBLISHED = 'PUBLISHED',
  DRAFT = 'DRAFT',
  DELETED = 'DELETED',
}

export enum UserStatus {
  GOOD = 'good',
  FROZEN = 'frozen',
}

export enum ContentSort {
  DESC = 'desc',
  RELATED = 'related',
}

/**
 * 内容类型
 */
export enum ContentKind {
  // 日历中使用的类型
  CALENDAR = -1,
  ARTICLE = 1,
  VIEWPOINT,
  VIDEO,
  AUDIO,
  AUDIO_EPISODE,
  POST,
  COMMENT,
  LINK,
  REPORT,
  DATA,
  ROADMAP,
  FAQ,
  ROADSHOW,
  TAG,
  FILE,
  USER,
  PUBLISHER,
  FINANCE_EVENT,
  TOPIC,
  // 订阅外源
  EXTERNAL_SOURCE,
  // 外源分组
  EXTERNAL_SOURCE_GROUP,
  // 外源文章
  EXTERNAL_ITEM,
  CALENDAR_TOPIC,
  DATA_SET,
  DATA_CHART,
  WALLSTREET_ARTICLES = 26,
  WALLSTREET_LIVES,
  REPORT_FEED = 28,
  // A股投资者问答
  ASHARE_QA = 29,
  // A股上市公司公告
  STOCK_A_FINANCIAL_REPORT = 30,
  // 交易台
  TRADER_FEED = 31,
  // 港股财务报告
  STOCK_HK_FINANCIAL_REPORT = 33,
  REPORT_IMAGE = 35,
  // a股和港股的研报摘要（没有报告）
  STOCK_A_HK_RESEARCH_ABSTRACT = 36,
  // 图片流
  IMAGE_STREAM = 37,
  // 美股财务报告
  STOCK_US_FINANCIAL_REPORT = 38,
  // 国内券商报告
  CN_BROKER_REPORT = 39,
  // 券商个股报告
  CN_BROKER_INDIVIDUAL_REPORT = 41,
}

/**
 * 常用内容类型
 */
export const CommonContentKinds = [
  ContentKind.ARTICLE,
  ContentKind.VIEWPOINT,
  ContentKind.VIDEO,
  ContentKind.REPORT,
  ContentKind.AUDIO_EPISODE,
  ContentKind.LINK,
  ContentKind.DATA,
  ContentKind.ROADSHOW,
]

export enum MessageAction {
  /** 评论消息 */
  COMMENT = 0,
  /** 点赞消息 */
  LIKE = 1,
  /** @消息 */
  AT = 2,
  /** 关注消息 */
  FOLLOW = 3,
  /** 系统消息 */
  SYSTEM = 4,
}

export enum CalendarImportance {
  LEVEL_1 = 1,
  LEVEL_2 = 2,
  LEVEL_3 = 3,
}

/** 列表通用类型 */
export interface ListResponse<T> {
  count: number
  anchor?: string
  list: T[]
  page?: number
}
export interface ItemsResponse<T> {
  total_count: number
  page_info?: PageInfo
  items: T[]
}

export interface nodesResponse<T> {
  total_count?: number
  page_info?: PageInfo
  nodes: T[]
}

export interface tasksResponse<T> {
  total_count: number
  tasks: T[]
}

export interface GraphqlResponse<T> {}
export interface UserListResponse {
  total_count: number
  page_info: PageInfo
  users: User[]
}
export interface PageInfo {
  has_next_page?: boolean
  end_cursor?: string
  page?: number
}
export enum OrderState {
  COMPLETE = 'COMPLETE',
  UNPAID = 'UNPAID',
}
export interface OrderRequestParams extends PageRequestParam {
  state?: OrderState
  payChannel?: PayChannel
  platform?: Platform
  userId?: number
  timeStart?: string
  timeEnd?: string
  sort?: string
  orderId?: string
  keywords?: string
}
export interface OrderItemsResponse extends ItemsResponse<Order> {
  anchor: string
  distinct_user_count: number
  total_amount: number
  total_count: number
}
export interface UserRequestParams extends PageRequestParam {
  keyword?: string
}
export interface TagListRequestParam extends PageRequestParam {
  keyword?: string
  isTopic?: boolean
  page: number
}

export interface TagRelationRequestParam {
  tag_relations: TagRelations[]
}

export interface VideoListRequestParam extends PageRequestParam {
  keyword?: string
  page: number
}
export interface HomeSlogan {
  text: string
  image: string
  url: string
}
export interface FileInfo {
  id?: number
  filename?: string
  qiniu_key: string
  qiniu_token: string
  qiniu_url: string
  meta: MediaMeta
}

export interface QiniuFileInfo {
  format: QiniuFileFormat
}
export interface QiniuFileFormat {
  duration: string
  bit_rate: string
  size: string
}
export interface RelatedTag {
  cover_url?: string
  description?: string
  id?: number
  is_private?: boolean
  is_source_group?: boolean
  is_topic?: boolean
  name?: string
}
export interface TagParam {
  id?: number
  title: string
  name?: string
  cover?: string
  description?: string
  topic?: boolean
}
export interface SearchParams extends PageRequestParam {
  keyword?: string
}
export interface NotificationParams {
  title: string
  subtitle?: string
  body?: string
  platform?: string
  extra: NotificationParamsExtra
  start_time?: string
  expire_time?: string
}

export interface NotificationParamsExtra {
  url?: string
}
export enum TagID {
  /**
   * 特殊id，用户不可见
   */
  MATRIX,
}

/**
 * tag内分组，控制tag下内容的分类
 */
export enum TagGroup {
  /**
   * 所有分组，做特殊处理
   */
  ALL = 99,
  NONE = 0,
  /**
   * 首页精选信息流
   */
  FEATURED_FLOW = 1,
  /**
   * 最新更新
   */
  LATEST = 2,
  /**
   * 典藏库
   */
  TREASURY,
  /**
   * FAQ
   */
  FAQ,
  /**
   * 路线图
   */
  ROADMAP,
  /**
   * 研究文章列表
   */
  RESEARCH_ARTICLES,
  /**
   * 研究视频列表
   */
  RESEARCH_VIDEOS,
  /**
   * 路演组别
   */
  ROADSHOW,
  /**
   * 报告组别
   */
  REPORT,
  /**
   * 链接组别
   */
  LINK,
  /**
   * 短讯（金融日历，观点）
   */
  FLASH,
  /**
   * 机构报告
   */
  INSTITUTION_REPORT,
  /**
   * matrix终端
   */
  MATRIX_TERMINAL,
  /**
   * web首页动态
   */
  WEBSITE_HOME,
  /**
   * 原创内容
   */
  ORIGINAL_CONTENT,
  /**
   * 金融事件
   */
  FINANCE_EVENT,
  /**
   * 投行研报
   */
  DOCUMENT_IB_REPORT,
  /**
   * 文献
   */
  DOCUMENT_ARCHIVE,
  /**
   * 企业报告
   */
  DOCUMENT_COMPANY,
  /**
   * AI生成内容
   */
  AI_GENERATED_CONTENT,
  /**
   * 观点（市场热点）
   */
  VIEWPOINT,
  /**
   * 新华财经日报
   */
  XINHUA_FINANCE_DAILY_REPORT = 22,
  /**
   * 图片流中的图片
   */
  FLOW_CHART_IMAGE = 23,
  /**
   * 电话会
   */
  EARNING_CALL = 24,
  /**
   * 近期投资者问答日报
   */
  A_SHARE_QA_SUMMARY = 25,
  /**
   * 纪要及实录
   */
  TRANSCRIPT = 26,
  /**
   * 动态发布
   */
  POSTS = 27,
}

/**
 * 老版本文章内容字段，之后废弃
 */
export type ArticleType = 'article' | 'tweet'

export type UseLazyQueryReturn<TResult, TVariables> = {
  load: (
    document?: DocumentNode | null,
    variables?: TVariables | null,
    options?: UseQueryOptions | null
  ) => false | Promise<TResult>
  result: Ref<TResult | undefined>
  loading: Ref<boolean>
  networkStatus: Ref<number | undefined>
  error: Ref<ApolloError | null>
}
export type CombinedQueryReturn<
  TResult,
  TVariables extends OperationVariables
> = UseQueryReturn<TResult, TVariables> &
  Partial<UseLazyQueryReturn<TResult, TVariables>>
